export interface Stage2MacSecretsReadyRecoveryRequest {
  readonly schemaVersion: 2;
  readonly phase: 'secrets-ready';
  priorTokenHash: string;
}

export type Stage2MacSuccessorRecoveryPhase =
  | 'identity-ready'
  | 'registration-bound'
  | 'registered'
  | 'manifest-verified';

export interface Stage2MacSuccessorRecoveryRequest {
  readonly schemaVersion: 3;
  readonly journalSchemaVersion: 2 | 3;
  readonly action: 'reset-to-identity-ready';
  readonly phase: Stage2MacSuccessorRecoveryPhase;
  readonly priorTokenHash: string;
  readonly priorExpectedReleaseSha: string;
  readonly priorExpectedReleaseChannel: string;
}

// Kept as a source-compatible alias while callers move from the original
// single-checkpoint recovery to the crash-complete successor recovery.
export type Stage2MacManifestVerifiedRecoveryRequest = Stage2MacSuccessorRecoveryRequest;

export type Stage2MacRecoveryRequest =
  | Stage2MacSecretsReadyRecoveryRequest
  | Stage2MacSuccessorRecoveryRequest;

export interface Stage2MigrationRequest {
  path: string;
  body: Record<string, unknown>;
}

export interface Stage2ContainmentAcknowledgementSnapshot {
  readonly homeId: string;
  readonly edgeId: string;
  readonly acknowledgedAt: number;
  readonly usableUntil: number;
}

export type Stage2MigrationMode = 'initial' | 'linux-resume';

export function stage2LinuxResumeAvailable(securityPlatform: unknown): boolean {
  return securityPlatform === 'linux';
}

export const STAGE2_CONDUIT_BOOTSTRAP_PURPOSE =
  'stage2-blind-conduit-publisher-bootstrap-v1' as const;
export const STAGE2_BLIND_CONDUIT_RECOVERY_PURPOSE =
  'stage2-blind-conduit-recovery-v1' as const;
export const STAGE2_CONTAINMENT_ACKNOWLEDGEMENT_WINDOW_SECONDS = 5 * 60;

export interface Stage2RequestTicket {
  readonly scope: string;
  readonly generation: number;
}

export interface Stage2ConsumedCommand {
  readonly command: string;
  readonly ticket: Stage2RequestTicket;
}

export function stage2MigrationCommandUsableUntil(expiresAt: number): number {
  if (!Number.isSafeInteger(expiresAt) || expiresAt <= 120) {
    throw new Error('controlled-migration expiry is invalid');
  }
  return expiresAt - 120;
}

export function stage2MacBootstrapCommandUsableUntil(expiresAt: number): number {
  if (!Number.isSafeInteger(expiresAt) || expiresAt <= 225) {
    throw new Error('controlled-migration bootstrap expiry is invalid');
  }
  return expiresAt - 225;
}

export function stage2CommandSecondsRemaining(
  commandUsableUntil: number,
  nowEpochSeconds: number,
): number {
  if (!Number.isSafeInteger(commandUsableUntil)
      || commandUsableUntil < 0
      || !Number.isSafeInteger(nowEpochSeconds)
      || nowEpochSeconds < 0) {
    throw new Error('controlled-migration countdown is invalid');
  }
  return Math.max(0, commandUsableUntil - nowEpochSeconds);
}

function exactOperatorIdentifier(value: string, name: string): string {
  const trimmed = value.trim();
  const pattern = name === 'homeId'
    ? /^[A-Za-z0-9]{3,32}$/
    : /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
  if (!trimmed || trimmed !== value || !pattern.test(trimmed)) {
    throw new Error(`${name} is invalid for Stage 2 migration authority`);
  }
  return trimmed;
}

function stage2Scope(homeId: string, edgeId: string): string {
  return `${exactOperatorIdentifier(homeId, 'homeId')}\u0000${exactOperatorIdentifier(edgeId, 'edgeId')}`;
}

/**
 * Component-local, non-secret acknowledgement fence for the operator's exact
 * host-containment proof. A scope change invalidates it synchronously, and a
 * migration attempt consumes it before beginning the request. React state may
 * mirror the returned snapshot for display, but cannot confer authority.
 */
export class Stage2ContainmentAcknowledgement {
  private scope: string;
  private mode: Stage2MigrationMode = 'initial';
  private acknowledgement: Stage2ContainmentAcknowledgementSnapshot | null = null;

  constructor(homeId: string, edgeId: string) {
    this.scope = stage2Scope(homeId, edgeId);
  }

  setScope(homeId: string, edgeId: string): boolean {
    const nextScope = stage2Scope(homeId, edgeId);
    if (nextScope === this.scope) return false;
    this.scope = nextScope;
    this.acknowledgement = null;
    return true;
  }

  setMode(mode: Stage2MigrationMode): boolean {
    if (mode !== 'initial' && mode !== 'linux-resume') {
      this.invalidate();
      throw new Error('migration acknowledgement mode is invalid');
    }
    if (mode === this.mode) return false;
    this.mode = mode;
    this.invalidate();
    return true;
  }

  acknowledge(
    homeId: string,
    edgeId: string,
    nowEpochSeconds = Math.floor(Date.now() / 1000),
  ): Stage2ContainmentAcknowledgementSnapshot {
    this.setScope(homeId, edgeId);
    if (!Number.isSafeInteger(nowEpochSeconds)
        || nowEpochSeconds < 0
        || nowEpochSeconds > Number.MAX_SAFE_INTEGER
          - STAGE2_CONTAINMENT_ACKNOWLEDGEMENT_WINDOW_SECONDS) {
      this.acknowledgement = null;
      throw new Error('containment acknowledgement clock is invalid');
    }
    this.acknowledgement = Object.freeze({
      homeId: exactOperatorIdentifier(homeId, 'homeId'),
      edgeId: exactOperatorIdentifier(edgeId, 'edgeId'),
      acknowledgedAt: nowEpochSeconds,
      usableUntil: nowEpochSeconds + STAGE2_CONTAINMENT_ACKNOWLEDGEMENT_WINDOW_SECONDS,
    });
    return this.acknowledgement;
  }

  isUsable(
    homeId: string,
    edgeId: string,
    nowEpochSeconds = Math.floor(Date.now() / 1000),
  ): boolean {
    if (!Number.isSafeInteger(nowEpochSeconds) || nowEpochSeconds < 0) return false;
    const acknowledgement = this.acknowledgement;
    return acknowledgement !== null
      && stage2Scope(homeId, edgeId) === this.scope
      && acknowledgement.homeId === homeId
      && acknowledgement.edgeId === edgeId
      && nowEpochSeconds >= acknowledgement.acknowledgedAt
      && nowEpochSeconds < acknowledgement.usableUntil;
  }

  consume(
    homeId: string,
    edgeId: string,
    nowEpochSeconds = Math.floor(Date.now() / 1000),
  ): boolean {
    const usable = this.isUsable(homeId, edgeId, nowEpochSeconds);
    this.acknowledgement = null;
    return usable;
  }

  invalidate(): void {
    this.acknowledgement = null;
  }
}

const HEX_40 = /^[0-9a-f]{40}$/;
const HEX_64 = /^[0-9a-f]{64}$/;
const ROOT_PROJECTION_MAX_JOURNAL_BYTES = 128 * 1024;
const ROOT_PROJECTION_MAX_INPUT_CHARACTERS = 16 * 1024;
const INVALID_SUCCESSOR_PROJECTION =
  'Stage 2 recovery projection is invalid or does not match this home and edge.';
const SUCCESSOR_RECOVERY_PHASES = new Set<Stage2MacSuccessorRecoveryPhase>([
  'identity-ready',
  'registration-bound',
  'registered',
  'manifest-verified',
]);

const ROOT_PROJECTION_KEYS_V2 = [
  'authenticatedUser',
  'candidateBootstrapNodeMode',
  'expectedChannel',
  'expectedSha',
  'journal',
  'migration',
  'phase',
  'platform',
  'priorBootstrapNodeMode',
  'priorReadySha',
  'priorRootTrust',
  'priorTokenHash',
  'registration',
  'release',
  'schemaVersion',
  'secretSlotsPresent',
] as const;
const ROOT_PROJECTION_KEYS_V3 = [
  ...ROOT_PROJECTION_KEYS_V2,
  'migrationSourceSha',
] as const;

function isExactRecord(value: unknown, exactKeys: readonly string[]): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const actualKeys = Object.keys(value).sort();
  const expectedKeys = [...exactKeys].sort();
  return actualKeys.length === expectedKeys.length
    && actualKeys.every((key, index) => key === expectedKeys[index]);
}

function invalidSuccessorProjection(): Error {
  // Deliberately constant: neither the projection nor its prior-token binding
  // can reach an exception message, rendered error, log, or telemetry sink.
  return new Error(INVALID_SUCCESSOR_PROJECTION);
}

/**
 * Parse a complete, secret-free root-journal projection and collapse it to
 * the only client authority one of the four identity-bound checkpoints may
 * send. Schema 2 and schema 3 are deliberately disjoint: schema 2 retains its
 * original exact shape, while schema 3 must carry the distinct migration
 * source SHA used only in the full projection digest. Every projected field is
 * checked so an omitted, extra, stale, cross-home, or cross-edge value fails
 * closed; the returned request carries the journal schema binding but cannot
 * expose the migration source SHA or other projection-only metadata.
 */
export function parseStage2SuccessorRecoveryProjection(
  projectionJson: string,
  homeId: string,
  edgeId: string,
): Stage2MacSuccessorRecoveryRequest {
  const exactHomeId = exactOperatorIdentifier(homeId, 'homeId');
  const exactEdgeId = exactOperatorIdentifier(edgeId, 'edgeId');

  try {
    if (typeof projectionJson !== 'string'
        || projectionJson.length < 2
        || projectionJson.length > ROOT_PROJECTION_MAX_INPUT_CHARACTERS) {
      throw invalidSuccessorProjection();
    }
    const projection: unknown = JSON.parse(projectionJson);
    if (projection === null || typeof projection !== 'object' || Array.isArray(projection)) {
      throw invalidSuccessorProjection();
    }
    const projectionRecord = projection as Record<string, unknown>;
    const schemaVersion = projectionRecord.schemaVersion;
    const exactProjection = schemaVersion === 2
      ? isExactRecord(projectionRecord, ROOT_PROJECTION_KEYS_V2)
      : schemaVersion === 3
      ? isExactRecord(projectionRecord, ROOT_PROJECTION_KEYS_V3)
      : false;
    if (!exactProjection) throw invalidSuccessorProjection();

    const expectedSha = projectionRecord.expectedSha;
    const expectedChannel = projectionRecord.expectedChannel;
    const migrationSourceSha = projectionRecord.migrationSourceSha;
    const phase = projectionRecord.phase;
    if (typeof phase !== 'string'
        || !SUCCESSOR_RECOVERY_PHASES.has(phase as Stage2MacSuccessorRecoveryPhase)
        || projectionRecord.platform !== 'darwin'
        || projectionRecord.migration !== true
        || projectionRecord.priorRootTrust !== 'preprotocol'
        || projectionRecord.priorReadySha !== null
        || projectionRecord.priorBootstrapNodeMode !== '0700'
        || projectionRecord.candidateBootstrapNodeMode !== null
        || typeof projectionRecord.authenticatedUser !== 'string'
        || !/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/.test(projectionRecord.authenticatedUser)
        || typeof projectionRecord.priorTokenHash !== 'string'
        || !HEX_64.test(projectionRecord.priorTokenHash)
        || typeof expectedSha !== 'string'
        || !HEX_40.test(expectedSha)
        || expectedChannel !== `canary-${expectedSha}`
        || (schemaVersion === 3 && (
          typeof migrationSourceSha !== 'string'
          || !HEX_40.test(migrationSourceSha)
          || migrationSourceSha === expectedSha
        ))) {
      throw invalidSuccessorProjection();
    }

    const journal = projectionRecord.journal;
    if (!isExactRecord(journal, ['mode', 'ownerGid', 'ownerUid', 'size'])
        || journal.mode !== '0600'
        || journal.ownerUid !== 0
        || journal.ownerGid !== 0
        || !Number.isSafeInteger(journal.size)
        || (journal.size as number) < 2
        || (journal.size as number) > ROOT_PROJECTION_MAX_JOURNAL_BYTES) {
      throw invalidSuccessorProjection();
    }

    const secretSlots = projectionRecord.secretSlotsPresent;
    if (!isExactRecord(secretSlots, ['edgeKey', 'signerKey', 'systemAttestationKey'])
        || secretSlots.edgeKey !== true
        || secretSlots.signerKey !== true
        || secretSlots.systemAttestationKey !== true) {
      throw invalidSuccessorProjection();
    }

    const registration = projectionRecord.registration;
    if (!isExactRecord(registration, ['edgeId', 'homeId', 'requestBound', 'requestEdgeId'])
        || registration.requestEdgeId !== exactEdgeId
        || registration.edgeId !== exactEdgeId
        || registration.homeId !== exactHomeId
        || registration.requestBound !== true) {
      throw invalidSuccessorProjection();
    }

    const release = projectionRecord.release;
    if (phase === 'manifest-verified') {
      if (!isExactRecord(release, ['channel', 'manifestBound', 'sha', 'tarballBound'])
          || release.sha !== expectedSha
          || release.channel !== expectedChannel
          || release.manifestBound !== true
          || release.tarballBound !== true) {
        throw invalidSuccessorProjection();
      }
    } else if (release !== null) {
      throw invalidSuccessorProjection();
    }

    return Object.freeze({
      schemaVersion: 3,
      journalSchemaVersion: schemaVersion as 2 | 3,
      action: 'reset-to-identity-ready',
      phase: phase as Stage2MacSuccessorRecoveryPhase,
      priorTokenHash: projectionRecord.priorTokenHash,
      priorExpectedReleaseSha: expectedSha,
      priorExpectedReleaseChannel: expectedChannel,
    });
  } catch {
    throw invalidSuccessorProjection();
  }
}

export const parseStage2ManifestVerifiedProjection = parseStage2SuccessorRecoveryProjection;

function canonicalProjectionJson(value: unknown): string {
  const normalize = (candidate: unknown): unknown => {
    if (Array.isArray(candidate)) return candidate.map(normalize);
    if (candidate !== null && typeof candidate === 'object') {
      return Object.fromEntries(
        Object.entries(candidate as Record<string, unknown>)
          .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
          .map(([key, child]) => [key, normalize(child)]),
      );
    }
    return candidate;
  };
  return `${JSON.stringify(normalize(value))}\n`;
}

async function sha256Hex(value: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error('Secure projection hashing is unavailable in this browser.');
  }
  // WebCrypto requires an ArrayBuffer-backed view. Copy once so a caller can
  // never hand the digest API a SharedArrayBuffer-backed or aliased view.
  const digestInput = new Uint8Array(value);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', digestInput);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Build the bootstrap-only request. The full secret-free projection is
 * validated and canonically hashed in memory, then collapsed to its schema-3
 * recovery binding plus digest. Release SHA/channel, artifact keys, versions,
 * and URLs remain exclusively server-derived.
 */
export async function stage2ConduitBootstrapRequest(
  projectionJson: string,
  homeId: string,
  edgeId: string,
): Promise<Stage2MigrationRequest> {
  const exactHomeId = exactOperatorIdentifier(homeId, 'homeId');
  const exactEdgeId = exactOperatorIdentifier(edgeId, 'edgeId');
  const recovery = parseStage2SuccessorRecoveryProjection(
    projectionJson,
    exactHomeId,
    exactEdgeId,
  );
  let projection: unknown;
  try {
    projection = JSON.parse(projectionJson);
  } catch {
    throw invalidSuccessorProjection();
  }
  const projectionSha256 = await sha256Hex(
    new TextEncoder().encode(canonicalProjectionJson(projection)),
  );
  return {
    path: `/homes/${encodeURIComponent(exactHomeId)}/edge/migration-token`,
    body: {
      expectedEdgeId: exactEdgeId,
      projectionSha256,
      purpose: STAGE2_CONDUIT_BOOTSTRAP_PURPOSE,
      recovery,
    },
  };
}

export function stage2SecretsReadyRecoveryRequest(
  priorTokenHash: string,
): Stage2MacSecretsReadyRecoveryRequest {
  if (!HEX_64.test(priorTokenHash)) {
    throw new Error('priorTokenHash is invalid for Stage 2 migration recovery');
  }
  return Object.freeze({
    schemaVersion: 2,
    phase: 'secrets-ready',
    priorTokenHash,
  });
}

/**
 * Component-local authority fence. Scope changes synchronously invalidate every
 * outstanding response and held command. A command is consumed atomically
 * before the asynchronous clipboard boundary, so a double click cannot copy it
 * twice while the first write is pending.
 */
export class Stage2MigrationSession {
  private scope: string;
  private generation = 0;
  private requestInFlight = false;
  private command: Readonly<{
    value: string;
    usableUntilEpochSeconds: number;
  }> | null = null;

  constructor(homeId: string, edgeId: string) {
    this.scope = stage2Scope(homeId, edgeId);
  }

  setScope(homeId: string, edgeId: string): boolean {
    const nextScope = stage2Scope(homeId, edgeId);
    if (nextScope === this.scope) return false;
    this.scope = nextScope;
    this.generation += 1;
    this.requestInFlight = false;
    this.command = null;
    return true;
  }

  invalidate(): void {
    if (this.scope === '' && !this.requestInFlight && this.command === null) return;
    this.scope = '';
    this.generation += 1;
    this.requestInFlight = false;
    this.command = null;
  }

  begin(homeId: string, edgeId: string): Stage2RequestTicket | null {
    this.setScope(homeId, edgeId);
    if (this.requestInFlight || this.command !== null) return null;
    this.requestInFlight = true;
    this.command = null;
    this.generation += 1;
    return Object.freeze({ scope: this.scope, generation: this.generation });
  }

  isCurrent(ticket: Stage2RequestTicket): boolean {
    return ticket.scope === this.scope && ticket.generation === this.generation;
  }

  finish(ticket: Stage2RequestTicket): boolean {
    if (!this.isCurrent(ticket)) return false;
    this.requestInFlight = false;
    return true;
  }

  holdCommand(
    ticket: Stage2RequestTicket,
    command: string,
    usableUntilEpochSeconds: number,
    nowEpochSeconds = Math.floor(Date.now() / 1000),
  ): boolean {
    if (!this.isCurrent(ticket)
        || typeof command !== 'string'
        || command.length === 0
        || !Number.isSafeInteger(usableUntilEpochSeconds)
        || !Number.isSafeInteger(nowEpochSeconds)
        || nowEpochSeconds < 0
        || nowEpochSeconds >= usableUntilEpochSeconds) {
      return false;
    }
    this.command = Object.freeze({ value: command, usableUntilEpochSeconds });
    return true;
  }

  takeCommand(
    nowEpochSeconds = Math.floor(Date.now() / 1000),
  ): Stage2ConsumedCommand | null {
    const heldCommand = this.command;
    // Wipe before every return and before the caller can cross an asynchronous
    // clipboard boundary. An invalid clock sample also fails closed.
    this.command = null;
    if (heldCommand === null
        || !Number.isSafeInteger(nowEpochSeconds)
        || nowEpochSeconds < 0
        || nowEpochSeconds >= heldCommand.usableUntilEpochSeconds) {
      return null;
    }
    return Object.freeze({
      command: heldCommand.value,
      ticket: Object.freeze({ scope: this.scope, generation: this.generation }),
    });
  }

  discardCommand(): void {
    this.command = null;
  }
}

/**
 * Build the complete home-scoped request accepted by the fail-closed Stage 2
 * migration-token endpoint. Ordinary Linux and Darwin migrations never supply
 * release authority. Successor recovery may carry only the prior projection-bound SHA,
 * canary channel, and exact journal schema; current release authority, TTLs,
 * URLs, and security state remain server-derived.
 */
export function stage2MigrationRequest(
  homeId: string,
  edgeId: string,
  recovery?: Stage2MacRecoveryRequest,
): Stage2MigrationRequest {
  const exactHomeId = exactOperatorIdentifier(homeId, 'homeId');
  const exactEdgeId = exactOperatorIdentifier(edgeId, 'edgeId');
  if (recovery !== undefined) {
    if (!recovery || !HEX_64.test(recovery.priorTokenHash)) {
      throw new Error('priorTokenHash is invalid for Stage 2 migration recovery');
    }
    let recoveryBody: Stage2MacRecoveryRequest;
    if (recovery.schemaVersion === 2) {
      if (recovery.phase !== 'secrets-ready'
          || Object.keys(recovery).sort().join(',') !== 'phase,priorTokenHash,schemaVersion') {
        throw new Error('secrets-ready recovery authority is invalid');
      }
      recoveryBody = stage2SecretsReadyRecoveryRequest(recovery.priorTokenHash);
    } else if (recovery.schemaVersion === 3) {
      if (recovery.action !== 'reset-to-identity-ready'
          || (recovery.journalSchemaVersion !== 2 && recovery.journalSchemaVersion !== 3)
          || !SUCCESSOR_RECOVERY_PHASES.has(recovery.phase)
          || !HEX_40.test(recovery.priorExpectedReleaseSha)
          || recovery.priorExpectedReleaseChannel !== `canary-${recovery.priorExpectedReleaseSha}`
          || Object.keys(recovery).sort().join(',') !== [
            'action',
            'journalSchemaVersion',
            'phase',
            'priorExpectedReleaseChannel',
            'priorExpectedReleaseSha',
            'priorTokenHash',
            'schemaVersion',
          ].join(',')) {
        throw new Error('successor recovery authority is invalid');
      }
      recoveryBody = Object.freeze({ ...recovery });
    } else {
      throw new Error('unsupported Stage 2 migration recovery schema');
    }
    return {
      path: `/homes/${encodeURIComponent(exactHomeId)}/edge/migration-token`,
      body: {
        expectedEdgeId: exactEdgeId,
        ...(recoveryBody.schemaVersion === 3
          ? { purpose: STAGE2_BLIND_CONDUIT_RECOVERY_PURPOSE }
          : {}),
        recovery: recoveryBody,
      },
    };
  }
  // The backend derives and re-proves the platform from the frozen Edge row.
  // Never add a client-selectable expectedPlatform field here.
  return {
    path: `/homes/${encodeURIComponent(exactHomeId)}/edge/migration-token`,
    body: { expectedEdgeId: exactEdgeId },
  };
}

export function stage2MigrationConfirmation(
  homeId: string,
  edgeId: string,
  recovery?: Stage2MacRecoveryRequest,
): string {
  const exactHomeId = exactOperatorIdentifier(homeId, 'homeId');
  const exactEdgeId = exactOperatorIdentifier(edgeId, 'edgeId');
  const label = recovery?.schemaVersion === 3
    ? `mint a one-time ${recovery.phase} broker-only recovery payload that resets this Mac edge to identity-ready`
    : recovery?.phase === 'secrets-ready'
    ? 'mint a one-time secrets-ready journal-bound recovery command for this Mac edge'
    : 'mint a one-time root migration command for the backend-derived edge platform';
  return [
    `Stage 2 controlled migration: ${label}?`,
    '',
    `Home: ${exactHomeId}`,
    `Edge: ${exactEdgeId}`,
    ...(recovery === undefined ? [] : ['Platform requirement: Mac (Darwin, server-verified)']),
    ...(recovery === undefined ? [
      'Operator prerequisite: exact host-local pre-mint containment receipt verified, then every paired-home pin strongly re-read as the same exact canary.',
      'The backend does not and cannot prove that host-local containment receipt.',
    ] : []),
    recovery?.schemaVersion === 3
      ? [
        `Prior expected SHA: ${recovery.priorExpectedReleaseSha}`,
        `Prior expected channel: ${recovery.priorExpectedReleaseChannel}`,
        `Run immediately. The 300-second authority resets only the exactly bound ${recovery.phase} checkpoint to identity-ready.`,
      ].join('\n')
      : recovery?.phase === 'secrets-ready'
      ? 'Run immediately. The 300-second token can rotate the prior journal binding only while more than 120 seconds remain.'
      : 'Run immediately. Controlled-migration claim admission closes when 120 seconds remain on the 300-second token. The backend derives and re-proves whether the frozen edge is Linux or Mac.',
  ].join('\n');
}

export function stage2LinuxResumeConfirmation(
  homeId: string,
  edgeId: string,
  securityPlatform: unknown,
): string {
  if (!stage2LinuxResumeAvailable(securityPlatform)) {
    throw new Error('Linux journal resume requires a server-reported Linux edge');
  }
  return [
    'Stage 2 controlled migration: mint a replacement command to resume this Linux journal?',
    '',
    `Home: ${exactOperatorIdentifier(homeId, 'homeId')}`,
    `Edge: ${exactOperatorIdentifier(edgeId, 'edgeId')}`,
    'Operator prerequisite: the exact root-owned schema-4 journal remains at contained, secrets-ready, identity-ready, registration-bound, registered, or manifest-verified for the same target SHA and canary.',
    'The root hold and containment receipt remain intact; the old updater is stopped, with no updater locks, pending deploy, publish transaction, or active enrollment lock. The frozen source is unchanged and every paired-home pin was strongly re-read as that same exact canary.',
    'The prior journal tokenExpiresAt has passed. The earlier command admission cutoff is not token expiry.',
    'The backend cannot prove these host-local prerequisites. The installer re-proves the exact journal and containment, and replaces only expired token authority without changing the target or phase.',
    'Run immediately. Claim admission closes when 120 seconds remain on the replacement 300-second token. Do not remove the journal or hold.',
  ].join('\n');
}

export function stage2ConduitBootstrapConfirmation(
  homeId: string,
  edgeId: string,
  recovery: Stage2MacSuccessorRecoveryRequest,
): string {
  const exactHomeId = exactOperatorIdentifier(homeId, 'homeId');
  const exactEdgeId = exactOperatorIdentifier(edgeId, 'edgeId');
  if (recovery.schemaVersion !== 3
      || (recovery.journalSchemaVersion !== 2 && recovery.journalSchemaVersion !== 3)
      || recovery.action !== 'reset-to-identity-ready'
      || !SUCCESSOR_RECOVERY_PHASES.has(recovery.phase)) {
    throw new Error('successor recovery authority is invalid');
  }
  return [
    'Stage 2 controlled migration: prepare the one-time trusted-console Mac bootstrap?',
    '',
    `Home: ${exactHomeId}`,
    `Edge: ${exactEdgeId}`,
    `Prior checkpoint: ${recovery.phase}`,
    `Prior expected SHA: ${recovery.priorExpectedReleaseSha}`,
    '',
    'This creates no migration bearer. The first sudo transaction assumes a trusted local Terminal and user session; stop if that session may be compromised.',
    'After sudo starts, the fixed stager authenticates exact publisher artifacts. Run it immediately, then regenerate and paste a fresh root projection before minting broker-only recovery.',
  ].join('\n');
}
